import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@apollo/client";
import GetUsers from "../queries/GetUsers";
import Select from "react-select";
import Spinner from "./Spinner";
import writeXlsxFile from "write-excel-file";
import { useAppContext } from "../AppContext";
// reactstrap components
import {
  Card,
  CardHeader,
  CardBody,
  Table,
  Row,
  Col,
  FormGroup,
  Button,
} from "reactstrap";

const SurveyCompletions = () => {
  const { error, loading, data, refetch } = useQuery(GetUsers, {
    // fetchPolicy: "network-only",
    variables: {
      survey_language: null,
      trial_type: null,
      survey_type: null,
    },
  });
  const [selectedLanguage, setSelectedLanguage] = useState({
    value: null,
    label: "None",
  });
  const [selectedTrialType, setSelectedTrialType] = useState({
    value: null,
    label: "None",
  });
  const [selectedSurveyType, setSelectedSurveyType] = useState({
    value: null,
    label: "None",
  });
  const users = useRef([]);
  const { displayToast, closeToast } = useAppContext();
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const runRefetch = async () => {
      let variables;

      if (
        (!selectedLanguage || selectedLanguage.value === null) &&
        (!selectedTrialType || selectedTrialType.value === null) &&
        (!selectedSurveyType || selectedSurveyType.value === null)
      ) {
        if (users.current.length === 0) return;

        variables = {
          survey_language: null,
          trial_type: null,
          survey_type: null,
        };
      } else {
        variables = {
          survey_language: selectedLanguage?.value ?? null,
          trial_type: selectedTrialType?.value ?? null,
          survey_type: selectedSurveyType?.value ?? null,
        };
      }

      setIsFetching(true);

      try {
        await refetch(variables);
      } finally {
        setIsFetching(false);
      }
    };

    runRefetch();
  }, [selectedLanguage, selectedTrialType, selectedSurveyType, refetch]);

  if (loading) return <Spinner />;
  if (error) {
    console.log("error:");
    console.log(error);
    return <p>Something Went Wrong...</p>;
  }

  // Assuming `data.users` is an array of user objects
  users.current = data.users.filter(
    (user) =>
      user.surveys && Array.isArray(user.surveys) && user.surveys.length > 0,
  );

  function checkCompletionStatus(survey) {
    if (survey == null) {
      return "No Survey Found";
    }

    if (survey.completed_date != null) {
      return "Completed";
    }

    if (
      survey.section_1_completed_date == null &&
      survey.section_1_completed_date == null &&
      survey.section_1_completed_date == null
    ) {
      return "Not Started";
    }

    return "In Progress";
  }

  function displayDate(date) {
    if (date == null) {
      return;
    }

    // parse the time stored
    const parsedDate = JSON.parse(date);
    // convert the string to a date
    const actualDate = new Date(parsedDate);
    // check if time is anythng other than 1... time is # of seconds since Jan 1 1970, no reason for it to be 1 but it will be 1 if failing
    if (actualDate.getTime() === 1) {
      return;
    }

    // reached here because it has been completed and has valid date
    return `${actualDate.getUTCMonth() + 1}/${actualDate.getUTCDate()}/${actualDate.getFullYear()}`;
  }

  function displaySurvey(survey, sIndex, uIndex) {
    // retrieving status so that it can be used to color code both the title and the feedback.
    const status = checkCompletionStatus(survey);

    return (
      <tr key={`${uIndex}_${sIndex}`} colSpan={12}>
        <td
          style={{ color: status !== "Completed" ? "red" : "" }}
          className="w-75"
        >
          {survey.title}
        </td>
        <td className="w-25">
          {status === "Completed" && displayDate(survey.completed_date)}
        </td>
      </tr>
    );
  }

  function displayUsers() {
    if (isFetching) {
      return (
        <div className="d-flex flex-column p-5 my-5 h-100 w-100 justify-content-center align-items-center">
          <Spinner />
          <h3 className="text-muted mt-3">Updating results...</h3>
        </div>
      );
    }

    return users.current.map((user, uIndex) => {
      if (user.role === "admin") return null;

      return (
        <Table key={user.user_name} responsive bordered striped>
          <thead>
            <tr>
              <th colSpan={12}>
                <div
                  className="d-flex justify-content-center w-100 font-weight-bold"
                  style={{
                    fontSize: ".85rem",
                  }}
                >
                  {user.user_name}
                </div>
              </th>
            </tr>
          </thead>
          <thead>
            <tr>
              <th className="w-75">Module</th>
              <th className="w-25">Completed Date</th>
            </tr>
          </thead>
          <tbody>
            {user.surveys.map((survey, sIndex, uIndex) => {
              return displaySurvey(survey, sIndex);
            })}
          </tbody>
        </Table>
      );
    });
  }

  function generateExcel() {
    let success = users.current !== null && users.current.length !== 0;

    const toast = {
      title: success ? "Success" : "Error",
      color: success ? "bg-success" : "bg-danger",
      icon: success ? "check-bold" : "fat-remove",
      message: success ? "XML is being generated" : "No data to place in XML",
    };

    displayToast(toast);

    setTimeout(() => {
      closeToast();
    }, 5000);

    if (!success) return;

    let tableData = [];

    users.current.forEach((user, dIndex) => {
      // objects.push(data.problems);
      // schemas.push(schema)
      // sheets.push(data.title.replace(/^Course I, /, '').slice(0, 30));

      tableData.push([
        {
          value: user.user_name,
          span: 2,
          backgroundColor: "#484a5c",
          color: "#ffffff",
          align: "center",
          width: 36,
        },
        null,
      ]);

      tableData.push([
        {
          type: String,
          value: "Module",
          align: "center",
          width: 12,
          backgroundColor: "#a8aabd",
          color: "#ffffff",
        },
        {
          type: String,
          value: "Completed Date",
          align: "center",
          width: 12,
          backgroundColor: "#a8aabd",
          color: "#ffffff",
        },
      ]);

      user.surveys.forEach((survey, pIndex) => {
        // parse the time stored
        const parsedDate = JSON.parse(survey.completed_date);
        // convert the string to a date
        let actualDate = new Date(parsedDate);
        // check if time is anythng other than 1... time is # of seconds since Jan 1 1970, no reason for it to be 1 but it will be 1 if failing
        if (actualDate.getTime() === 1 || actualDate.getTime() === 0) {
          actualDate = "";
        }

        // Prepare the objects
        let titleObject = {
          type: String,
          value: survey.title,
          width: 12,
        };

        let dateObject = {
          type: Date,
          format: "mm/dd/yyyy",
          value: actualDate,
          align: "center",
          width: 12,
        };

        // Conditionally add color: "red" to the first object if actualDate === ""
        if (actualDate === "") {
          titleObject.color = "#FF0000";
        }

        // Now push the prepared array of objects into tableData
        tableData.push([titleObject, dateObject]);
      });
    });

    // Get the current date
    const currentDate = new Date();

    // Format the date as YYYY-MM-DD
    const formattedDate = currentDate.toISOString().split("T")[0]; // This will give you a string like "2023-04-01"

    // Create the filename by including the formatted date
    const fileName = `completion_status_${formattedDate}.xlsx`;

    // Use the fileName in your writeXlsxFile function
    writeXlsxFile(tableData, {
      fileName: fileName,
    });
  }

  return (
    <>
      <Button color="primary" onClick={() => generateExcel()}>
        Create Excel
      </Button>
      <Card className="bg-secondary shadow">
        <CardHeader className="bg-white border-0">
          <Row className="align-items-center">
            <Col sm="6" lg="4">
              <FormGroup>
                <label className="form-control-label" htmlFor="input-language">
                  Language
                </label>
                <Select
                  value={selectedLanguage}
                  onChange={(selectedLanguage) => {
                    setSelectedLanguage(selectedLanguage);
                  }}
                  options={[
                    {
                      value: null,
                      label: "None",
                    },
                    {
                      value: "arabic",
                      label: "Arabic",
                    },
                    {
                      value: "korean",
                      label: "Korean",
                    },
                    {
                      value: "russian",
                      label: "Russian",
                    },
                  ]}
                  className="form-control-alternative"
                  id="input-language"
                />
              </FormGroup>
            </Col>
            <Col sm="6" lg="4">
              <FormGroup>
                <label className="form-control-label" htmlFor="input-trial">
                  Trial Type
                </label>
                <Select
                  value={selectedTrialType}
                  onChange={(selectedTrialType) => {
                    setSelectedTrialType(selectedTrialType);
                  }}
                  options={[
                    {
                      value: null,
                      label: "None",
                    },
                    {
                      value: "individual",
                      label: "Individual",
                    },
                    {
                      value: "group",
                      label: "Group",
                    },
                  ]}
                  className="form-control-alternative"
                  id="input-trial"
                />
              </FormGroup>
            </Col>
            {/* <Col sm="6" lg="4">
              <FormGroup>
                <label
                  className="form-control-label"
                  htmlFor="input-survey"
                >
                  Survey Type
                </label>
                <Select
                  value={selectedSurveyType}
                  onChange={(selectedSurveyType) => {
                    setSelectedSurveyType(selectedSurveyType);
                  }}
                  options={[
                    {
                      value: null,
                      label: "None",
                    },
                    {
                      value: "imi",
                      label: "IMI",
                    },
                    {
                      value: "introduction",
                      label: "Introduction",
                    },
                    {
                      value: "assessment",
                      label: "Assessment",
                    },
                    {
                      value: "posttest",
                      label: "Posttest",
                    },
                  ]}
                  className="form-control-alternative"
                  id="input-survey"
                />
              </FormGroup>
            </Col> */}
          </Row>
        </CardHeader>
        <CardBody className="p-0">
          {users.current !== null && users.current.length > 0 ? (
            <>
              <div className="d-flex justify-content-center py-2 w-100 font-weight-bold table-dark">
                Course II
              </div>
              {displayUsers()}
            </>
          ) : isFetching ? (
            <div className="d-flex flex-column p-5 my-5 h-100 w-100 justify-content-center align-items-center">
              <Spinner />
              <h3 className="text-muted mt-3">Loading results...</h3>
            </div>
          ) : (
            <div className="d-flex flex-column p-5 my-5 h-100 w-100 justify-content-center align-items-center">
              <h1>
                <i className="ni ni-bell-55 text-muted" />
              </h1>
              <h3 className="text-muted">
                Please select what you'd like to view
              </h3>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default SurveyCompletions;
